import * as React from "react";
import { RecommendationKind } from "../App";
import { params } from "../context";
import {
	ComponentViewLog,
	dropdownOpen,
	logDropdownChange,
	recommendationLoaded,
} from "../Helper/logging";
import RecommendationService from "../Helper/RecommendationService";
import { TBSRecommendation } from "./CSHPTBSCard";
import { QnARecommendation } from "./QnACard";
import { ChangeHandlerData, RecommendationCategories } from "./Select";
import Slider, { RecommendationSet, SliderProps } from "./Slider";
import SliderHeading, { SliderHeadingProps } from "./SliderHeading";

export type RecommendationItem = TBSRecommendation | QnARecommendation;

export interface RecommendationProps {
	theme?: boolean;
	kind?: RecommendationKind;
	categories?: Omit<RecommendationCategories, "kind">;
	initialState?: RecommendationState;
	loaded?: boolean;
	context?: string;
}

export interface RecommendationList {
	kind: RecommendationKind;
	set?: RecommendationSet;
}

interface RecommendationState {
	selected?: string;
	categories?: Omit<RecommendationCategories, "kind">;
	data?: Omit<RecommendationList, "kind">;
	error?: boolean;
}

class Recommendation extends React.Component<
	RecommendationProps,
	RecommendationState
> {
	static displayName = "Recommendation";
	themeCSSClass: string = "";
	selectedGroupId: number;
	recommendationRef: React.RefObject<HTMLDivElement>;
	componentViewLog: typeof ComponentViewLog;
	forceReload: boolean;
	loaded: boolean;

	constructor(props: RecommendationProps) {
		super(props);
		this.forceReload = false;
		this.recommendationRef = React.createRef();
		this.themeCSSClass = this.props.theme
			? this.props.kind === RecommendationKind.QnA
				? "colored coloredQnA"
				: "colored coloredTBS"
			: "";

		this.state = {
			selected: this.props.categories && this.props.categories.selected,
			categories: this.props.categories,
			...this.props.initialState,
		};
		this.loaded = false;
		this.componentViewLog = Object.create(ComponentViewLog);
	}
	getRecommendationRef() {
		return this.recommendationRef.current;
	}

	render() {
		const { props, state } = this;

		const isLoading =
			(!state.data && !state.error) || (state.data && !state.data.set);

		let title = "";

		const sliderHeadingProps: SliderHeadingProps = {
			error: state.error,
		};
		if (state.categories) {
			switch (props.kind) {
				case RecommendationKind.TBS:
					title = "Based on your progress in";
					break;
				case RecommendationKind.QnA:
					title = "Up next for you in";
					break;
			}
			sliderHeadingProps.data = {
				title,
				categories: {
					...state.categories,
					kind: this.props.kind,
					clickHandler: () => dropdownOpen(this.props.kind),
					selected: state.selected,
					loading: isLoading,
					changeHandler: data => this.changeRecommendationCategory(data),
				},
			};
		}

		const sliderProps: SliderProps = {
			error: state.error,
			reload: this.reload.bind(this),
		};

		if (state.data) {
			sliderProps.data = {
				kind: props.kind,
				set: state.data.set,
			};
		}

		return (
			<section
				data-test={`cshp-recommendation-${
					this.props.kind === RecommendationKind.QnA ? "qna" : "tbs"
				}`}
				ref={this.recommendationRef}
				className={`csrec-slider ${this.themeCSSClass} ${
					isLoading ? "csrec-slider-loading" : ""
				} ${this.props.context}`}
			>
				<div className="csrec-slider-container">
					{this.props.context !== "CSHP" && (
						<div className="csrec-slider-header">Recommendations</div>
					)}
					<div className="csrec-slider-body">
						<SliderHeading {...sliderHeadingProps} />
						<Slider {...sliderProps} />
					</div>
				</div>
			</section>
		);
	}

	async componentDidUpdate(
		prevProps: RecommendationProps,
		prevState: RecommendationState
	) {
		if (
			!this.state.data &&
			(!this.state.error ||
				prevState.selected !== this.state.selected ||
				this.forceReload)
		) {
			try {
				this.forceReload = false;
				await this.setRecommendations();
			} catch (e) {
				this.setErrorState();
			}
		} else if (prevState.selected !== this.state.selected) {
			this.setState({
				data: undefined,
				error: false,
			});
		} else if (this.componentViewLog.componentViewLoggingDone) {
			this.viewAndDropdownChangeLogging();
		}
	}

	componentDidMount() {
		!this.state.data && this.props.loaded && this.setRecommendations();
		this.componentViewLog.init(this.getRecommendationRef(), () => {
			this.viewAndDropdownChangeLogging();
		});
	}

	async setRecommendations() {
		try {
			const recommendationList = await RecommendationService.fetchRecommendations(
				{
					kind: this.props.kind,
					selected: this.state.selected,
					limit: params.sliderCount,
				}
			);
			this.setState({
				data: recommendationList,
				error: false,
			});
			if (!this.loaded) {
				this.autoScroll();
			}
			this.loaded = true;
		} catch (e) {
			this.setState({
				error: true,
			});
		}
	}

	autoScroll() {
		if (
			params.context !== "CSHP" ||
			typeof document === "undefined" ||
			typeof window === "undefined" ||
			!document.referrer
		) {
			return;
		}

		try {
			const referrerURL = new URL(document.referrer);
			const cameFromQuestionOrVideoPage = /\/homework-help\/.*-q?\d+/.test(
				referrerURL.pathname
			);
			const shouldAutoscrollToTBS =
				window.localStorage.getItem("tbs_cshp_autoscroll") &&
				this.props.kind === RecommendationKind.TBS;
			const shouldAutoscrollToQnA =
				window.localStorage.getItem("qna_cshp_autoscroll") &&
				this.props.kind === RecommendationKind.QnA;
			if (cameFromQuestionOrVideoPage) {
				if (shouldAutoscrollToTBS) {
					window.localStorage.removeItem("tbs_cshp_autoscroll");
				} else if (shouldAutoscrollToQnA) {
					window.localStorage.removeItem("qna_cshp_autoscroll");
				}

				if (shouldAutoscrollToTBS || shouldAutoscrollToQnA) {
					window.setTimeout(() => {
						this.getRecommendationRef().scrollIntoView(true);
						if (window.scrollY) {
							window.scroll(0, window.scrollY - 60);
						}
					}, 1000);
				}
			}
		} catch (e) {
			console.error(`Error scrolling to CSHP Recs: ${e}`);
		}
	}

	reload() {
		this.forceReload = true;
		this.setState({
			selected: this.state.selected,
			error: false,
		});
	}

	changeRecommendationCategory(data: ChangeHandlerData) {
		let value = data.value;
		let isbn13 = null;
		if (this.props.kind === RecommendationKind.TBS) {
			const [isbn, groupId] = value.split(":");
			this.selectedGroupId = parseInt(groupId);
			isbn13 = isbn;
		}
		this.setState({
			selected: data.value,
		});
		logDropdownChange({
			kind: this.props.kind,
			groupId:
				this.props.kind === RecommendationKind.TBS
					? this.selectedGroupId
					: parseInt(data.value),
			isbn13: this.props.kind === RecommendationKind.TBS && isbn13,
		});
	}

	setErrorState() {
		this.setState({
			error: true,
		});
	}

	componentDidCatch(error: any) {
		this.setErrorState();
	}

	viewAndDropdownChangeLogging() {
		if (
			!this.state.error &&
			this.state.data &&
			this.state.data.set &&
			this.state.data.set.items &&
			this.state.data.set.items.length > 0
		) {
			const [isbn13, groupid] =
				this.props.kind === RecommendationKind.TBS
					? this.state.selected.split(":")
					: [undefined, this.state.selected];
			const recommendationId = this.state.data.set.id;
			recommendationLoaded({
				kind: this.props.kind,
				recommendationId,
				groupid: parseInt(groupid),
				isbn13,
				categoriesCount: this.state.categories.items.length,
				itemsCount: this.state.data.set.items.length,
			});
		}
	}
}

export default Recommendation;
